package web.fstech.onthecoffee.auth

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import web.fstech.onthecoffee.SupabaseService
import web.fstech.onthecoffee.mvi.MviViewModel

class AuthViewModel(
    initialRoute: String = "signin"
) : MviViewModel<AuthState, AuthIntent, AuthEffect>(
    AuthState(
        currentTab = determineInitialTab(initialRoute),
        statusMessage = if (AuthTab.fromRoute(initialRoute) == AuthTab.DELETE_ACCOUNT && !SupabaseService.isSignedIn()) {
            "Please sign in first to access account deletion."
        } else null
    )
) {

    companion object {
        private fun determineInitialTab(initialRoute: String): AuthTab {
            val target = AuthTab.fromRoute(initialRoute)
            if (target == AuthTab.DELETE_ACCOUNT && !SupabaseService.isSignedIn()) {
                return AuthTab.SIGN_IN
            }
            return target
        }
    }

    override fun handleIntent(intent: AuthIntent) {
        when (intent) {
            is AuthIntent.EmailChanged -> updateState { copy(email = intent.email) }
            is AuthIntent.PasswordChanged -> updateState { copy(password = intent.password) }
            is AuthIntent.TabChanged -> {
                if (intent.tab == AuthTab.DELETE_ACCOUNT && !SupabaseService.isSignedIn()) {
                    updateState {
                        copy(
                            currentTab = AuthTab.SIGN_IN,
                            statusMessage = "Please sign in first to access account deletion.",
                            isLoading = false
                        )
                    }
                    sendEffect(AuthEffect.NavigateToRoute("signin"))
                } else {
                    updateState {
                        copy(
                            currentTab = intent.tab,
                            statusMessage = null,
                            isLoading = false
                        )
                    }
                }
            }
            is AuthIntent.DeleteConfirmedChanged -> updateState { copy(deleteConfirmed = intent.confirmed) }
            is AuthIntent.ClearStatus -> updateState { copy(statusMessage = null) }
            AuthIntent.SubmitSignIn -> performSignIn()
            AuthIntent.SubmitSignUp -> performSignUp()
            AuthIntent.SubmitForgotPassword -> performForgotPassword()
            AuthIntent.SubmitDeleteAccount -> performDeleteAccount()
        }
    }

    private fun performSignIn() {
        if (currentState.email.isBlank() || currentState.password.isBlank()) {
            updateState { copy(statusMessage = "Please enter both email and password.") }
            return
        }
        viewModelScope.launch {
            updateState { copy(isLoading = true, statusMessage = null) }
            val result = SupabaseService.signIn(currentState.email, currentState.password)
            if (result.isSuccess) {
                updateState {
                    copy(
                        isLoading = false,
                        isSuccess = true,
                        statusMessage = "Signed in successfully!"
                    )
                }
                sendEffect(AuthEffect.ShowToast("Signed in successfully!"))
            } else {
                val err = result.exceptionOrNull()?.message ?: "Unknown error"
                updateState { copy(isLoading = false, isSuccess = false, statusMessage = "Error: $err") }
            }
        }
    }

    private fun performSignUp() {
        if (currentState.email.isBlank() || currentState.password.isBlank()) {
            updateState { copy(statusMessage = "Please enter both email and password.") }
            return
        }
        viewModelScope.launch {
            updateState { copy(isLoading = true, statusMessage = null) }
            val result = SupabaseService.signUp(currentState.email, currentState.password)
            if (result.isSuccess) {
                updateState {
                    copy(
                        isLoading = false,
                        isSuccess = true,
                        statusMessage = "Account created! Check your email for verification."
                    )
                }
                sendEffect(AuthEffect.ShowToast("Account created successfully!"))
            } else {
                val err = result.exceptionOrNull()?.message ?: "Unknown error"
                updateState { copy(isLoading = false, isSuccess = false, statusMessage = "Error: $err") }
            }
        }
    }

    private fun performForgotPassword() {
        if (currentState.email.isBlank()) {
            updateState { copy(statusMessage = "Please enter your email address.") }
            return
        }
        viewModelScope.launch {
            updateState { copy(isLoading = true, statusMessage = null) }
            val result = SupabaseService.forgotPassword(currentState.email)
            if (result.isSuccess) {
                updateState {
                    copy(
                        isLoading = false,
                        isSuccess = true,
                        statusMessage = "Password reset email sent!"
                    )
                }
                sendEffect(AuthEffect.ShowToast("Password reset email sent!"))
            } else {
                val err = result.exceptionOrNull()?.message ?: "Unknown error"
                updateState { copy(isLoading = false, isSuccess = false, statusMessage = "Error: $err") }
            }
        }
    }

    private fun performDeleteAccount() {
        if (!SupabaseService.isSignedIn()) {
            updateState {
                copy(
                    currentTab = AuthTab.SIGN_IN,
                    statusMessage = "Please sign in first to access account deletion."
                )
            }
            sendEffect(AuthEffect.NavigateToRoute("signin"))
            return
        }
        if (!currentState.deleteConfirmed) {
            updateState { copy(statusMessage = "Please confirm deletion first.") }
            return
        }
        viewModelScope.launch {
            updateState { copy(isLoading = true, statusMessage = null) }
            val result = SupabaseService.deleteAccount()
            if (result.isSuccess) {
                updateState {
                    copy(
                        isLoading = false,
                        isSuccess = true,
                        statusMessage = "Account deleted successfully."
                    )
                }
                sendEffect(AuthEffect.ShowToast("Account deleted successfully."))
            } else {
                val err = result.exceptionOrNull()?.message ?: "Unknown error"
                updateState { copy(isLoading = false, isSuccess = false, statusMessage = "Error: $err") }
            }
        }
    }
}
